"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import CommonLogo from "./CommonLogo"
import { Col, Form, FormGroup, Input, Label, Row, Spinner } from "reactstrap"
import { Btn, H4, P } from "../../../../AbstractElements"
import {
  CreateAccount,
  phone,
  phoneNumberPlaceholder,
  CreateYourAccount,
  EmailAddress,
  EmailsPlaceHolder,
  FirstName,
  LastName,
  SignIn,
  YourName,
} from "../../../../utils/Constant"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import AuthService from "../../../../Service/AuthService"

const CommonRegisterForm = ({ alignLogo }: any) => {
  const [searchParams] = useSearchParams()
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    agreed: false,
  })
  const [loading, setLoading] = useState(false)
  const [isFocalPersonSignup, setIsFocalPersonSignup] = useState(false)
  const [companyName, setCompanyName] = useState<string>("")

  useEffect(() => {
    const token = searchParams.get("token")
    if (token) {
      setIsFocalPersonSignup(true)
      decodeTokenAndGetCompanyName(token)
    }
  }, [searchParams])

  const decodeTokenAndGetCompanyName = (token: string) => {
    try {
      // Decode the Django signed token
      // Since it's a Django signed token, we need to extract the base64 part
      const parts = token.split(".")
      if (parts.length >= 2) {
        // Try to decode the payload part
        const payload = parts[0]
        try {
          const decoded = atob(payload)
          const tokenData = JSON.parse(decoded)
          if (tokenData.company_name) {
            setCompanyName(tokenData.company_name)
          }
        } catch (e) {
          // If direct decoding fails, the token might be more complex
          // Let's try a different approach - extract from the token string itself
          console.log("Direct decode failed, token might be Django signed")
        }
      }
    } catch (error) {
      console.error("Error decoding token:", error)
      // If decoding fails, we'll still show the form but without company name
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setLoading(true)
      const token = searchParams.get("token")

      // Prepare signup data
      const signupData = {
        ...formData,
        ...(token ? { token } : {}),
      }

      const response = await AuthService.signup(signupData)

      if (response.exhibitor_info || isFocalPersonSignup) {
        toast.success("Focal person account created successfully! Please check your email for verification.")
      } else if (token) {
        toast.error("Invalid or expired registration link. Please contact support.")
        return
      } else {
        toast.success("Verification email sent. Please check your inbox.")
      }

      // Reset the form
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        agreed: false,
      })
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || "Signup failed"
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-card login-dark">
      <div>
        <CommonLogo alignLogo={alignLogo} />
        <div className="login-main">
          {/* Company Name Display */}
          {isFocalPersonSignup && companyName && (
            <div className="text-center mb-4 p-2 bg-light rounded">
              <P className="mb-0 text-muted">
                <i className="fas fa-building me-2"></i>
                Your company is <strong className="text-primary">{companyName}</strong>
              </P>
            </div>
          )}

          <Form className="theme-form" onSubmit={handleSubmit}>
            <H4 className="text-center mb-3">{isFocalPersonSignup ? "Register as Focal Person" : CreateYourAccount}</H4>

            <FormGroup>
              <Label className="col-form-label pt-0">{YourName}</Label>
              <Row className="g-2">
                <Col xs="6">
                  <Input
                    type="text"
                    name="first_name"
                    placeholder={FirstName}
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                  />
                </Col>
                <Col xs="6">
                  <Input
                    type="text"
                    name="last_name"
                    placeholder={LastName}
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                  />
                </Col>
              </Row>
            </FormGroup>

            <FormGroup>
              <Label className="col-form-label">{EmailAddress}</Label>
              <Input
                type="email"
                name="email"
                placeholder={EmailsPlaceHolder}
                value={formData.email}
                onChange={handleChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label className="col-form-label">{phone}</Label>
              <Input
                type="text"
                name="phone"
                placeholder={phoneNumberPlaceholder}
                value={formData.phone}
                onChange={handleChange}
              />
            </FormGroup>

            <FormGroup className="mb-0">
              <Btn block color="primary" className="w-100" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating Account...
                  </>
                ) : isFocalPersonSignup ? (
                  "Register as Focal Person"
                ) : (
                  CreateAccount
                )}
              </Btn>
            </FormGroup>

            <P className="mt-3 mb-0 text-center">
              Already have an account?
              <Link className="ms-2" to={`/auth/login/`}>
                {SignIn}
              </Link>
            </P>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default CommonRegisterForm
